/**
 * Pure adapter functions: FHIR resources → normalized client portal billing types.
 *
 * All functions are defensive — missing fields produce partial but displayable
 * objects rather than throwing. No raw external IDs (Stripe, Candid) are
 * surfaced in the normalized output.
 */
import type {
  Account,
  Appointment,
  ChargeItem,
  Coverage,
  CoverageEligibilityResponse,
  DocumentReference,
  Encounter,
  ExplanationOfBenefit,
  Invoice,
  Organization,
  Resource,
} from '@medplum/fhirtypes';

import {
  APPOINTMENT_SERVICE_TYPE_SYSTEM,
  ASSESSMENT_TASK_EXTENSION,
  BILLING_ADJUSTMENT_EXTENSION,
  BILLING_CLIENT_PAID_EXTENSION,
  BILLING_CLIENT_RESPONSIBILITY_EXTENSION,
  BILLING_DOCUMENT_KIND_EXTENSION,
  BILLING_DUE_DATE_EXTENSION,
  BILLING_INSURANCE_PAID_EXTENSION,
  BILLING_INSURANCE_RESPONSIBILITY_EXTENSION,
  BILLING_PAYMENT_CHARGE_TYPE_CODES,
  BILLING_PAYMENT_CHARGE_TYPE_EXTENSION,
  BILLING_PAYMENT_METHOD_EXTENSION,
  BILLING_PAYMENT_STATUS_EXTENSION,
  BILLING_WRITE_OFF_EXTENSION,
  CPT_CODING_SYSTEM,
  UNIFY_BILLING_SYSTEM,
} from '@unify/fhir-model/constants';

import type {
  ClientPortalBillingDocument,
  ClientPortalBillingDocumentKind,
  ClientPortalBillingServiceLine,
  ClientPortalBillingStatus,
  ClientPortalCoverageSummary,
  ClientPortalInvoiceSummary,
  ClientPortalPaymentSummary,
  ClientPortalPaymentChargeType,
} from './clientPortalFhirTypes';
import { encounterPortalDateRaw } from './portalBillingEncounter';
import { preferredLabelOrFallback } from '../../../lib/loadPractitionerPreferredLabels';
import {
  calculateServiceLineAmountDue,
  formatServiceLineLabel,
  maskGroupNumber,
  maskMemberId,
} from './portalBillingModels';

// ---------------------------------------------------------------------------
// Extension helpers
// ---------------------------------------------------------------------------

function findExtension(resource: Resource, url: string) {
  // @ts-expect-error — FHIR resource may have extension
  const exts: { url?: string; valueInteger?: number; valueString?: string; valueBoolean?: boolean; valueDate?: string }[] =
    (resource as { extension?: unknown[] }).extension ?? [];
  return exts.find((e) => e.url === url);
}

export function extractMoneyFromExtension(resource: Resource, url: string): number | undefined {
  const ext = findExtension(resource, url);
  if (ext?.valueInteger != null) return ext.valueInteger;
  return undefined;
}

export function extractStringFromExtension(resource: Resource, url: string): string | undefined {
  const ext = findExtension(resource, url);
  return ext?.valueString ?? undefined;
}

export function extractBooleanFromExtension(resource: Resource, url: string): boolean | undefined {
  const ext = findExtension(resource, url);
  if (ext?.valueBoolean != null) return ext.valueBoolean;
  return undefined;
}

export function extractDateFromExtension(resource: Resource, url: string): string | undefined {
  const ext = findExtension(resource, url);
  return ext?.valueDate ?? undefined;
}

export function extractCodeFromExtension(resource: Resource, url: string): string | undefined {
  const ext = findExtension(resource, url) as { valueCode?: string } | undefined;
  return ext?.valueCode ?? undefined;
}

// ---------------------------------------------------------------------------
// Reference helpers
// ---------------------------------------------------------------------------

/** Safely extracts the ID from a FHIR reference string like "Patient/abc-123". */
export function safeReferenceId(reference: string | undefined, resourceType: string): string | undefined {
  if (!reference) return undefined;
  const prefix = `${resourceType}/`;
  if (reference.startsWith(prefix)) return reference.slice(prefix.length);
  return undefined;
}

// ---------------------------------------------------------------------------
// Billing status inference
// ---------------------------------------------------------------------------

function inferBillingStatus(fhirStatus: string | undefined, extStatus?: string | null): ClientPortalBillingStatus {
  const ext = extStatus?.toLowerCase();
  if (ext === 'overdue') return 'overdue';
  if (ext === 'due') return 'due';
  if (ext === 'paid') return 'paid';
  if (ext === 'partially-paid') return 'partially-paid';
  if (ext === 'processing') return 'processing';
  if (ext === 'void') return 'void';
  if (ext === 'refunded') return 'refunded';
  if (ext === 'insurance-pending' || ext === 'insurance-paid') return 'insurance-pending';

  switch (fhirStatus) {
    case 'issued':
    case 'draft':
      return 'due';
    case 'balanced':
      return 'paid';
    case 'cancelled':
      return 'void';
    default:
      return 'unknown';
  }
}

// ---------------------------------------------------------------------------
// Account → balance
// ---------------------------------------------------------------------------

/**
 * Extracts the client-facing balance from an Account resource.
 * Returns cents or 0 if not present.
 */
export function accountToBillingBalance(account: Account): number {
  const fromExt = extractMoneyFromExtension(account as Resource, BILLING_CLIENT_RESPONSIBILITY_EXTENSION);
  if (fromExt != null) return fromExt;
  // FHIR Account.balance is not in the R4 type; try to read gracefully.
  const raw = account as { balance?: { value?: number }[] };
  return (raw.balance?.[0]?.value ?? 0) * 100;
}

// ---------------------------------------------------------------------------
// Coverage → CoverageSummary
// ---------------------------------------------------------------------------

function cersForCoverage(
  cers: CoverageEligibilityResponse[],
  coverageId: string,
): CoverageEligibilityResponse[] {
  const ref = `Coverage/${coverageId}`;
  return cers.filter((r) => r.insurance?.[0]?.coverage?.reference === ref);
}

function latestCerForCoverage(cers: CoverageEligibilityResponse[]): CoverageEligibilityResponse | undefined {
  return [...cers].sort((a, b) => {
    const aMs = Date.parse(a.created ?? '');
    const bMs = Date.parse(b.created ?? '');
    return (Number.isNaN(bMs) ? 0 : bMs) - (Number.isNaN(aMs) ? 0 : aMs);
  })[0];
}

function benefitAmountsFromCer(cer: CoverageEligibilityResponse): {
  copayCents?: number;
  deductibleRemainingCents?: number;
} {
  const benefits = (cer.insurance?.[0]?.item ?? []).flatMap((item) => item.benefit ?? []);
  const copay = benefits.find((b) => b.type?.coding?.some((c) => c.code === 'copay'));
  const deductible = benefits.find((b) => b.type?.coding?.some((c) => c.code === 'deductible'));
  return {
    copayCents:
      copay?.allowedMoney?.value != null ? Math.round(copay.allowedMoney.value * 100) : undefined,
    deductibleRemainingCents:
      deductible?.allowedMoney?.value != null
        ? Math.round(deductible.allowedMoney.value * 100)
        : undefined,
  };
}

export function coverageToCoverageSummary(
  coverage: Coverage,
  payerOrg?: Organization,
  eligibilityResponses: CoverageEligibilityResponse[] = [],
): ClientPortalCoverageSummary {
  const payerName =
    payerOrg?.name ??
    coverage.payor?.[0]?.display ??
    'Insurance';

  const rawMemberId =
    coverage.subscriberId ??
    coverage.identifier?.find((i) => i.type?.coding?.some((c) => c.code === 'MB'))?.value;

  const rawGroupNumber = coverage.class?.find(
    (c) => c.type?.coding?.some((cd) => cd.code === 'group'),
  )?.value;

  const planClass = coverage.class?.find(
    (c) => c.type?.coding?.some((cd) => cd.code === 'plan'),
  );
  const planName = planClass?.name ?? planClass?.value;

  const relationship =
    coverage.relationship?.coding?.[0]?.display ??
    coverage.relationship?.text;

  const fhirStatus = coverage.status;
  const status: ClientPortalCoverageSummary['status'] =
    fhirStatus === 'active' ? 'active' : fhirStatus === 'cancelled' ? 'inactive' : 'unknown';

  const cer = coverage.id
    ? latestCerForCoverage(cersForCoverage(eligibilityResponses, coverage.id))
    : undefined;
  const benefits = cer ? benefitAmountsFromCer(cer) : {};

  return {
    id: coverage.id ?? 'coverage',
    priority:
      coverage.order === 1
        ? 'primary'
        : coverage.order === 2
          ? 'secondary'
          : coverage.order === 3
            ? 'tertiary'
            : undefined,
    payerName,
    memberIdMasked: maskMemberId(rawMemberId),
    groupNumberMasked: maskGroupNumber(rawGroupNumber),
    planName,
    relationship,
    periodStart: coverage.period?.start,
    periodEnd: coverage.period?.end,
    status,
    copayCents: benefits.copayCents,
    deductibleRemainingCents: benefits.deductibleRemainingCents,
  };
}

// ---------------------------------------------------------------------------
// Invoice → ClientPortalInvoiceSummary
// ---------------------------------------------------------------------------

export function invoiceToClientPortalInvoice(invoice: Invoice): ClientPortalInvoiceSummary {
  const res = invoice as Resource;
  const extStatus = extractStringFromExtension(res, BILLING_PAYMENT_STATUS_EXTENSION);
  const status = inferBillingStatus(invoice.status, extStatus);

  const totalCents =
    extractMoneyFromExtension(res, BILLING_CLIENT_RESPONSIBILITY_EXTENSION) ??
    Math.round((invoice.totalGross?.value ?? invoice.totalNet?.value ?? 0) * 100);

  const clientResponsibilityCents =
    extractMoneyFromExtension(res, BILLING_CLIENT_RESPONSIBILITY_EXTENSION) ?? totalCents;

  const clientPaidCents = extractMoneyFromExtension(res, BILLING_CLIENT_PAID_EXTENSION) ?? 0;
  const insuranceResponsibilityCents = extractMoneyFromExtension(res, BILLING_INSURANCE_RESPONSIBILITY_EXTENSION);
  const insurancePaidCents = extractMoneyFromExtension(res, BILLING_INSURANCE_PAID_EXTENSION);
  const adjustmentCents = extractMoneyFromExtension(res, BILLING_ADJUSTMENT_EXTENSION);
  const writeOffCents = extractMoneyFromExtension(res, BILLING_WRITE_OFF_EXTENSION);

  const amountDueCents = Math.max(0, clientResponsibilityCents - clientPaidCents);

  const canPay =
    amountDueCents > 0 &&
    (status === 'due' || status === 'overdue' || status === 'partially-paid');

  const dueDate =
    extractDateFromExtension(res, BILLING_DUE_DATE_EXTENSION) ??
    invoice.date;

  const invoiceNumber = invoice.identifier?.find(
    (i) => i.system?.includes('invoice'),
  )?.value;

  const description =
    invoice.type?.text ??
    invoice.type?.coding?.[0]?.display ??
    'Invoice';

  return {
    id: invoice.id ?? 'unknown',
    invoiceNumber,
    date: invoice.date,
    dueDate,
    description,
    totalCents,
    clientResponsibilityCents,
    clientPaidCents,
    insuranceResponsibilityCents,
    insurancePaidCents,
    adjustmentCents,
    writeOffCents,
    amountDueCents,
    status,
    canPay,
    sourceResource: `Invoice/${invoice.id}`,
  };
}

// ---------------------------------------------------------------------------
// ChargeItem → service line
// ---------------------------------------------------------------------------

export function chargeItemToServiceLine(
  chargeItem: ChargeItem,
  relatedEncounter?: Encounter,
  relatedInvoice?: Invoice,
  practitionerDisplayById: Map<string, string> = new Map(),
): ClientPortalBillingServiceLine {
  const res = chargeItem as Resource;
  const extStatus = extractStringFromExtension(res, BILLING_PAYMENT_STATUS_EXTENSION);
  const status = inferBillingStatus(chargeItem.status, extStatus);

  const dateOfService =
    chargeItem.occurrenceDateTime ??
    (chargeItem.occurrencePeriod as { start?: string } | undefined)?.start ??
    encounterPortalDateRaw(relatedEncounter ?? ({} as Encounter));

  const serviceType =
    (relatedEncounter ? resolveEncounterServiceTypeLabel(relatedEncounter) : undefined) ??
    chargeItem.code?.coding?.[0]?.display ??
    chargeItem.code?.text ??
    'Service';

  const cptCode = chargeItem.code?.coding?.find(
    (c) => c.system === CPT_CODING_SYSTEM || c.system?.includes('cpt'),
  )?.code;

  const performerRef = chargeItem.performer?.[0]?.actor;
  const performerId = safeReferenceId(performerRef?.reference, 'Practitioner');
  const performer = preferredLabelOrFallback(
    performerId,
    practitionerDisplayById,
    performerRef?.display,
  );

  const feeCents = Math.round((chargeItem.priceOverride?.value ?? 0) * 100) || undefined;

  const clientResponsibilityCents = extractMoneyFromExtension(res, BILLING_CLIENT_RESPONSIBILITY_EXTENSION);
  const clientPaidCents = extractMoneyFromExtension(res, BILLING_CLIENT_PAID_EXTENSION) ?? 0;
  const amountDueCents =
    clientResponsibilityCents != null
      ? calculateServiceLineAmountDue({ clientResponsibilityCents, clientPaidCents })
      : undefined;

  const invoiceId = relatedInvoice?.id;
  const encounterId = safeReferenceId(chargeItem.context?.reference, 'Encounter');
  const encounterKind = relatedEncounter
    ? resolveEncounterKind(relatedEncounter)
    : looksLikeAssessmentChargeItem(chargeItem)
      ? 'assessment'
      : 'appointment';

  return {
    id: chargeItem.id ?? 'unknown',
    dateOfService: dateOfService || new Date().toISOString(),
    serviceType,
    provider: performer,
    cptCode,
    feeCents,
    clientResponsibilityCents,
    clientPaidCents,
    amountDueCents,
    insuranceResponsibilityCents: extractMoneyFromExtension(res, BILLING_INSURANCE_RESPONSIBILITY_EXTENSION),
    insurancePaidCents: extractMoneyFromExtension(res, BILLING_INSURANCE_PAID_EXTENSION),
    adjustmentCents: extractMoneyFromExtension(res, BILLING_ADJUSTMENT_EXTENSION),
    writeOffCents: extractMoneyFromExtension(res, BILLING_WRITE_OFF_EXTENSION),
    status,
    invoiceId,
    encounterId,
    chargeItemId: chargeItem.id,
    encounterKind,
  };
}

// ---------------------------------------------------------------------------
// Encounter → fallback service line
// ---------------------------------------------------------------------------

function looksLikeAssessmentChargeItem(chargeItem: ChargeItem): boolean {
  const label =
    chargeItem.code?.coding?.[0]?.display ??
    chargeItem.code?.text ??
    '';
  const t = label.trim().toLowerCase();
  if (!t) return false;
  if (t.includes('assessment')) return true;
  if (/\bphq-?9\b|\bgad-?7\b|\bphq-?a\b|\bscreening\b/.test(t)) return true;
  const hasCpt = chargeItem.code?.coding?.some(
    (c) => c.system === CPT_CODING_SYSTEM || c.system?.includes('cpt'),
  );
  return !hasCpt && t.length > 0;
}

function appointmentStatusToBillingStatus(
  status: Appointment['status'] | undefined,
): ClientPortalBillingStatus {
  switch (status) {
    case 'fulfilled':
    case 'checked-in':
      return 'paid';
    case 'booked':
    case 'pending':
    case 'arrived':
      return 'due';
    default:
      return 'unknown';
  }
}

/** Maps a calendar Appointment to a service line when no Encounter/ChargeItem exists yet. */
export function appointmentToServiceLine(
  appointment: Appointment,
  practitionerDisplayById: Map<string, string> = new Map(),
): ClientPortalBillingServiceLine {
  const serviceType =
    codeableConceptLabel(appointment.serviceType?.[0]) ??
    codeableConceptLabel(appointment.appointmentType) ??
    'Appointment';

  const practitioner = appointment.participant?.find((p) =>
    p.actor?.reference?.startsWith('Practitioner/'),
  );
  const practitionerId = safeReferenceId(practitioner?.actor?.reference, 'Practitioner');

  return {
    id: appointment.id ? `appointment-${appointment.id}` : 'unknown',
    dateOfService: appointment.start ?? new Date().toISOString(),
    serviceType,
    provider: preferredLabelOrFallback(
      practitionerId,
      practitionerDisplayById,
      practitioner?.actor?.display,
    ),
    status: appointmentStatusToBillingStatus(appointment.status),
    encounterKind: 'appointment',
  };
}

function hasAssessmentServiceTypeCoding(concept: { coding?: { system?: string; code?: string }[] } | undefined): boolean {
  return concept?.coding?.some(
    (c) => c.system === APPOINTMENT_SERVICE_TYPE_SYSTEM && c.code === 'assessment',
  ) ?? false;
}

/** Detects assessment Encounters created by the assessment-encounter-creation bot. */
export function isAssessmentEncounter(encounter: Encounter): boolean {
  if (encounter.extension?.some((e) => e.url === ASSESSMENT_TASK_EXTENSION)) {
    return true;
  }
  if (hasAssessmentServiceTypeCoding(encounter.serviceType)) {
    return true;
  }
  if (encounter.type?.some((t) => hasAssessmentServiceTypeCoding(t))) {
    return true;
  }
  // Assessment encounters link to a Task; session encounters link to Appointment.
  const hasTaskRef = encounter.basedOn?.some((r) => r.reference?.startsWith('Task/'));
  const hasAppointment = (encounter.appointment?.length ?? 0) > 0;
  return hasTaskRef === true && !hasAppointment;
}

export function resolveEncounterKind(encounter: Encounter): 'appointment' | 'assessment' {
  return isAssessmentEncounter(encounter) ? 'assessment' : 'appointment';
}

function codeableConceptLabel(
  concept: { text?: string; coding?: { code?: string; display?: string }[] } | undefined,
): string | undefined {
  const text = concept?.text?.trim();
  // Concepts copied from a ChargeItemDefinition carry the bare CPT/HCPCS code in
  // `text`, which is not a client-facing label — fall through to the descriptor.
  const isBareCode = !!text && (concept?.coding ?? []).some((c) => c.code === text);
  if (text && !isBareCode) return text;
  const display = concept?.coding?.[0]?.display?.trim();
  return display || text || undefined;
}

/** Resolves a client-facing service label from Encounter fields. */
export function resolveEncounterServiceTypeLabel(encounter: Encounter): string {
  const label =
    codeableConceptLabel(encounter.serviceType) ??
    codeableConceptLabel(encounter.type?.[0]);

  if (label) return label;
  if (isAssessmentEncounter(encounter)) return 'Assessment';
  return 'Appointment';
}

export function encounterToFallbackServiceLine(
  encounter: Encounter,
  practitionerDisplayById: Map<string, string>,
): ClientPortalBillingServiceLine {
  const dateOfService = encounterPortalDateRaw(encounter);
  const encounterKind = resolveEncounterKind(encounter);
  const serviceType = resolveEncounterServiceTypeLabel(encounter);

  const cptCode = encounter.type?.[0]?.coding?.find(
    (c) => c.system === CPT_CODING_SYSTEM || c.system?.includes('cpt'),
  )?.code;

  const part = encounter.participant?.find((p) =>
    p.individual?.reference?.startsWith('Practitioner/'),
  );
  const practitionerId = part?.individual?.reference?.startsWith('Practitioner/')
    ? part.individual.reference.slice('Practitioner/'.length)
    : undefined;
  const provider = preferredLabelOrFallback(
    practitionerId,
    practitionerDisplayById,
    part?.individual?.display,
  );

  const fhirStatus = encounter.status;
  let status: ClientPortalBillingStatus = 'unknown';
  if (fhirStatus === 'finished') status = 'paid';
  else if (fhirStatus === 'planned') status = 'due';

  return {
    id: encounter.id ?? 'unknown',
    dateOfService: dateOfService || new Date().toISOString(),
    serviceType,
    provider,
    cptCode,
    status,
    encounterId: encounter.id,
    encounterKind,
  };
}

// ---------------------------------------------------------------------------
// EOB → billing amounts
// ---------------------------------------------------------------------------

export interface EobBillingAmounts {
  clientResponsibilityCents?: number;
  insurancePaidCents?: number;
  adjustmentCents?: number;
}

export function eobToBillingAmounts(eob: ExplanationOfBenefit): EobBillingAmounts {
  let clientResponsibilityCents: number | undefined;
  let insurancePaidCents: number | undefined;
  let adjustmentCents: number | undefined;

  for (const total of eob.total ?? []) {
    const code = total.category?.coding?.[0]?.code;
    if (!code) continue;
    const cents = Math.round((total.amount?.value ?? 0) * 100);
    if (code === 'paidtoprovider' || code === 'benefit') {
      insurancePaidCents = cents;
    } else if (code === 'deductible' || code === 'coinsurance' || code === 'copay') {
      clientResponsibilityCents = (clientResponsibilityCents ?? 0) + cents;
    } else if (code === 'noncovered' || code === 'discount') {
      adjustmentCents = (adjustmentCents ?? 0) + cents;
    }
  }

  return { clientResponsibilityCents, insurancePaidCents, adjustmentCents };
}

// ---------------------------------------------------------------------------
// DocumentReference → BillingDocument
// ---------------------------------------------------------------------------

const STATEMENT_CATEGORY_CODES = new Set(['statement', 'patient-statement']);
const SUPERBILL_CATEGORY_CODES = new Set(['superbill']);
const EOB_CATEGORY_CODES = new Set(['eob', 'explanation-of-benefits', 'eob-document']);
const GFE_CATEGORY_CODES = new Set(['gfe', 'good-faith-estimate']);
const RECEIPT_CATEGORY_CODES = new Set(['receipt', 'payment-receipt']);

export function inferBillingDocumentKind(docRef: DocumentReference): ClientPortalBillingDocumentKind {
  // 1. Unify extension
  const res = docRef as Resource;
  const extKind = extractStringFromExtension(res, BILLING_DOCUMENT_KIND_EXTENSION);
  if (extKind) {
    if (extKind === 'statement') return 'statement';
    if (extKind === 'superbill') return 'superbill';
    if (extKind === 'eob') return 'eob';
    if (extKind === 'gfe') return 'gfe';
    if (extKind === 'receipt') return 'receipt';
    if (extKind === 'invoice-pdf') return 'invoice';
  }

  // 2. type coding
  const typeCode = docRef.type?.coding?.[0]?.code?.toLowerCase() ??
    docRef.type?.text?.toLowerCase();
  if (typeCode) {
    if (STATEMENT_CATEGORY_CODES.has(typeCode)) return 'statement';
    if (SUPERBILL_CATEGORY_CODES.has(typeCode)) return 'superbill';
    if (EOB_CATEGORY_CODES.has(typeCode)) return 'eob';
    if (GFE_CATEGORY_CODES.has(typeCode)) return 'gfe';
    if (RECEIPT_CATEGORY_CODES.has(typeCode)) return 'receipt';
  }

  // 3. category coding
  for (const cat of docRef.category ?? []) {
    for (const coding of cat.coding ?? []) {
      const c = coding.code?.toLowerCase() ?? '';
      if (STATEMENT_CATEGORY_CODES.has(c)) return 'statement';
      if (SUPERBILL_CATEGORY_CODES.has(c)) return 'superbill';
      if (EOB_CATEGORY_CODES.has(c)) return 'eob';
      if (GFE_CATEGORY_CODES.has(c)) return 'gfe';
      if (RECEIPT_CATEGORY_CODES.has(c)) return 'receipt';
    }
  }

  // 4. UNIFY_BILLING_SYSTEM identifier
  const billingId = docRef.identifier?.find((i) => i.system === UNIFY_BILLING_SYSTEM);
  if (billingId?.value) {
    const v = billingId.value.toLowerCase();
    if (v.includes('stmt') || v.includes('statement')) return 'statement';
    if (v.includes('superbill') || v.includes('sb-')) return 'superbill';
  }

  // 5. attachment title heuristic
  const title = docRef.content?.[0]?.attachment?.title?.toLowerCase() ?? '';
  if (title.includes('statement')) return 'statement';
  if (title.includes('superbill')) return 'superbill';
  if (title.includes('eob') || title.includes('explanation')) return 'eob';
  if (title.includes('gfe') || title.includes('good faith')) return 'gfe';
  if (title.includes('receipt')) return 'receipt';

  return 'other';
}

export function documentReferenceToBillingDocument(
  docRef: DocumentReference,
): ClientPortalBillingDocument {
  const kind = inferBillingDocumentKind(docRef);

  const attachment = docRef.content?.[0]?.attachment;
  const title =
    attachment?.title ??
    docRef.description ??
    docRef.type?.text ??
    'Document';

  const date = docRef.date ?? attachment?.creation;

  return {
    id: docRef.id ?? 'unknown',
    kind,
    title,
    date,
    documentReferenceId: docRef.id,
    url: attachment?.url,
    contentType: attachment?.contentType,
    size: attachment?.size,
  };
}

function inferPaymentChargeType(resource: Resource): ClientPortalPaymentChargeType {
  const raw = extractCodeFromExtension(resource, BILLING_PAYMENT_CHARGE_TYPE_EXTENSION);
  if (raw === 'auto-charge' || raw === 'manual') return raw;
  if (BILLING_PAYMENT_CHARGE_TYPE_CODES.includes(raw as (typeof BILLING_PAYMENT_CHARGE_TYPE_CODES)[number])) {
    return raw as ClientPortalPaymentChargeType;
  }
  return 'unknown';
}

function inferClientPaymentStatus(
  resource: Resource,
  fhirStatus?: string,
): ClientPortalPaymentSummary['status'] {
  const extStatus = extractStringFromExtension(resource, BILLING_PAYMENT_STATUS_EXTENSION)?.toLowerCase();
  if (extStatus === 'refunded') return 'refunded';
  if (extStatus === 'processing' || fhirStatus === 'draft') return 'processing';
  if (extStatus === 'paid' || fhirStatus === 'balanced') return 'succeeded';
  if (extStatus === 'failed') return 'failed';
  return 'succeeded';
}

function serviceLabelForInvoice(
  invoiceId: string,
  serviceLines: ClientPortalBillingServiceLine[],
  fallback?: string,
): string {
  const related = serviceLines.filter((line) => line.invoiceId === invoiceId);
  if (related.length === 0) return fallback ?? 'Service';
  return related.map((line) => formatServiceLineLabel(line)).join(', ');
}

function serviceLineIdsForInvoice(invoiceId: string, serviceLines: ClientPortalBillingServiceLine[]): string[] {
  return serviceLines.filter((line) => line.invoiceId === invoiceId).map((line) => line.id);
}

/** Build client-facing payment rows from ChargeItems and paid invoices. */
export function buildClientPortalPayments(input: {
  chargeItems: ChargeItem[];
  serviceLines: ClientPortalBillingServiceLine[];
  invoices: ClientPortalInvoiceSummary[];
}): ClientPortalPaymentSummary[] {
  const serviceByChargeId = new Map(
    input.serviceLines
      .filter((line) => line.chargeItemId)
      .map((line) => [line.chargeItemId!, line] as const),
  );
  const payments: ClientPortalPaymentSummary[] = [];

  for (const ci of input.chargeItems) {
    const res = ci as Resource;
    const amountCents = extractMoneyFromExtension(res, BILLING_CLIENT_PAID_EXTENSION) ?? 0;
    if (amountCents <= 0 || !ci.id) continue;

    const line = serviceByChargeId.get(ci.id);
    payments.push({
      id: `pay-ci-${ci.id}`,
      date:
        ci.occurrenceDateTime ??
        (ci.occurrencePeriod as { start?: string } | undefined)?.start ??
        new Date().toISOString(),
      amountCents,
      status: inferClientPaymentStatus(res, ci.status),
      methodLabel: extractStringFromExtension(res, BILLING_PAYMENT_METHOD_EXTENSION),
      serviceLabel: line ? formatServiceLineLabel(line) : (ci.code?.text ?? ci.code?.coding?.[0]?.display ?? 'Service'),
      chargeType: inferPaymentChargeType(res),
      chargeItemId: ci.id,
      invoiceId: line?.invoiceId,
      serviceLineIds: line ? [line.id] : undefined,
    });
  }

  const invoiceIdsWithChargePayments = new Set(
    payments.map((p) => p.invoiceId).filter((id): id is string => Boolean(id)),
  );

  for (const inv of input.invoices) {
    if (inv.clientPaidCents <= 0) continue;
    if (invoiceIdsWithChargePayments.has(inv.id)) continue;

    payments.push({
      id: `pay-inv-${inv.id}`,
      date: inv.date ?? new Date().toISOString(),
      amountCents: inv.clientPaidCents,
      status: inv.status === 'paid' ? 'succeeded' : inv.status === 'processing' ? 'processing' : 'succeeded',
      serviceLabel: serviceLabelForInvoice(inv.id, input.serviceLines, inv.description),
      chargeType: 'unknown',
      invoiceId: inv.id,
      serviceLineIds: serviceLineIdsForInvoice(inv.id, input.serviceLines),
    });
  }

  return payments.sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );
}

// ---------------------------------------------------------------------------
// Practitioner name helper (re-exported from portalBillingEncounter)
// ---------------------------------------------------------------------------
export { formatPractitionerHumanName } from './portalBillingEncounter';
